import './shared/resources/styles/index.scss';
import 'web-animations-js/web-animations.min';

import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NDDActionbarGlobalConfig, NDDActionbarGlobalConfigModule } from '@ndk/ng-actionbar/config';
import { NDKBreadcrumbGlobalConfigModule } from '@ndk/ng-breadcrumb';
import { NDDButtonsGlobalConfigModule } from '@ndk/ng-buttons/config/ndd-ng-buttons-global-config.module';
import { NDDDialogGlobalConfigModule } from '@ndk/ng-dialog';
import { RedirectToForbiddenHandler, RedirectToNotFoundHandler, RedirectToUnauthorizedHandler } from '@ndk/ng-error-interceptor/component/error-handler.service';
import { ErrorInterceptorModule } from '@ndk/ng-error-interceptor/component/error-interceptor.module';
import { StatusCodeEnum } from '@ndk/ng-error-interceptor/component/shared/models/error-handler.model';
import { NDDFormGlobalConfigModule } from '@ndk/ng-form/config';
import { NDDGridGlobalConfig, NDDGridGlobalConfigModule } from '@ndk/ng-grid/config';
import { NDDNotificationGlobalConfig, NDDNotificationGlobalConfigModule } from '@ndk/ng-notification';
import { NDDOffSideScreenGlobalConfigModule } from '@ndk/ng-off-side-screen/config';
import { NDDOffSidebarGlobalConfig } from '@ndk/ng-offsidebar/config/ndd-ng-offsidebar-global-config.model';
import { NDDOffSidebarGlobalConfigModule } from '@ndk/ng-offsidebar/config/ndd-ng-offsidebar.module';
import { NDDReportGlobalConfigModule } from '@ndk/ng-reports';
import { NDDRouterModule } from '@ndk/ng-router/component/common';
import { NdkStorageModule } from '@ndk/ng-storage';
import { NDDTitlebarGlobalConfigModule } from '@ndk/ng-titlebar';
import { NDDToastrModule } from '@ndk/ng-toastr';
import { NDDTooltipGlobalConfigModule } from '@ndk/ng-tooltip/config/ndd-ng-tooltip-global-config.module';
import { NDDTranslationService } from '@ndk/ng-translation';

import { CORE_CONFIG } from '@core/core.config';
import { CoreModule } from '@core/core.module';
import { ReportCurrencyService } from '@core/report-config/report-currency.service';
import { ReportLanguageService } from '@core/report-config/report-language.service';
import { ReportAccessTokenService } from '@core/report-config/report-token.service';
import { LocalStorageKeys } from '@core/storage/local-storage.enum';
import { TermsAndPolicyGuardService } from '@features/login/shared/terms-and-policy-guard.service';
import { TermsAndPolicyService } from '@features/login/shared/terms-and-policy.service';
import { PageForbiddenComponent } from '@features/page-forbidden/page-forbidden.component';
import { PageInternalServerErrorComponent } from '@features/page-internal-server-error/page-internal-server-error.component';
import { PageNotFoundComponent } from '@features/page-not-found/page-not-found.component';
import { PageUnauthorizedComponent } from '@features/page-unauthorized/page-unauthorized.component';
import { QuotaParameterService } from '@features/quota/shared/quota-parameter.service';
import { ReportNewCurrentUserService } from '@features/report-new/report-new-current-user-service';
import { CustomTranslationService } from '@shared/overrides/custom-translation.service';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FilterConfigLogicalOperators, FilterConfigOperatorValuesByType } from './shared/filter-configs/filter-operators.model';
import { SharedModule } from './shared/shared.module';

@NgModule({
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        SharedModule,
        CoreModule.forRoot({
            resources: { api: CORE_CONFIG.apiEndpoint + 'api/resources/' },
            filter: {
                resources: I18N_FILTER,
                operatorsByType: new FilterConfigOperatorValuesByType(),
                defaultLogicalOperator: FilterConfigLogicalOperators.none,
                logicalOperators: FilterConfigLogicalOperators.availableOperators,
            },
            // Translation
            buttons: I18N_BUTTONS,
            forms: I18N_FORMS,
            ndkForm: I18N_NDK_FORM,
            dialog: I18N_DIALOG,
            grid: I18N_GRID,
            settings: I18N_SETTINGS,
            editor: I18N_EDITOR,
            notification: I18N_NOTIFICATION,
            lightGrid: I18N_LIGHTGRID,
            reports: I18N_REPORTS,

            // Kendo Translation
            kendoGrid: I18N_KENDO_GRID,
            kendoDatePicker: I18N_KENDO_DATEPICKER,
            kendoTimePicker: I18N_KENDO_TIMEPICKER,
        }),
        NDDTitlebarGlobalConfigModule.forRoot({ closeIcon: 'fa fa-close' }),
        NDDFormGlobalConfigModule.forRoot({
            forbiddenRoute: 'page-forbidden', serverErrorRoute: 'page-forbidden', unauthorizedRoute: 'page-forbidden',
        }),
        NDDGridGlobalConfigModule.forRoot(<NDDGridGlobalConfig>{
            ndsMode: false,
            disableRouterLinkUnalterable: false,
            editColumnIcon: 'ndd-font ndd-font-edit',
            sortMoveIcon: 'ndd-font ndd-font-grip-lines',
            hideEditColumnWhenResponsive: true,
            defaultExportCsvIcon: 'ndd-font ndd-font-export-csv',
            hideExportWhenResponsive: true,
            allowCustomColumns: false,
            editColumnDescription: '',
            headerDropdownText: '',
            headerCancelButtonText: '',
            i18n: true,
            defaultExportPdfIcon: '',
        }),
        NDDNotificationGlobalConfigModule.forRoot(<NDDNotificationGlobalConfig>{
            buttonIconId: 'layout-navbar-notification',
            localStorageLanguageKey: LocalStorageKeys.CurrentLanguage,
            aspnetSignalRConnectionUrl: CORE_CONFIG.apiSignalrEndpoint + 'hub/users',
            aspnetSignalRConnectionMethodName: 'NotificationsChange',
            aspnetSignalRTokenName: LocalStorageKeys.Token,
        }),
        NDDReportGlobalConfigModule.forRoot({
            tokenProvider: ReportAccessTokenService,
            languageProvider: ReportLanguageService,
            currencyProvider: ReportCurrencyService,
            userProvider: ReportNewCurrentUserService,
            defaultFileName: null,
            standalonePath: 'standalone',
            /**
             * TODO: Implementar serviço para buscar a propriedade dataLimitForODataItems após conclusão da Issue pelo Labs:
             * https://tfs.ndd.com.br/tfs/NDD-PDICollection/NDK/_workitems/edit/9362
             */
            dataLimitForODataItems: '10000',
        }),
        NDKBreadcrumbGlobalConfigModule.forRoot({ ndsMode: false }),
        NDDTooltipGlobalConfigModule.forRoot({ ndsMode: false }),
        NdkStorageModule.forRoot({ useSessionStorage: false }),
        NDDButtonsGlobalConfigModule.forRoot({ ndsMode: false }),
        NDDOffSidebarGlobalConfigModule.forRoot(<NDDOffSidebarGlobalConfig>{ ndsMode: false }),
        NDDActionbarGlobalConfigModule.forRoot(<NDDActionbarGlobalConfig>{ ndsMode: false, angleDownIcon: 'fa fa-angle-down' }),
        NDDDialogGlobalConfigModule.forRoot({ ndsMode: false }),
        NDDOffSideScreenGlobalConfigModule.forRoot({ ndsMode: false, closeOnRouterChange: true }),
        NDDRouterModule,
        ErrorInterceptorModule.forRoot({
            handlers:
                [
                    {
                        code: StatusCodeEnum.NotFound,
                        handler: RedirectToNotFoundHandler,
                    },
                    {
                        code: StatusCodeEnum.Forbidden,
                        handler: RedirectToForbiddenHandler,
                    },
                    {
                        code: StatusCodeEnum.Unauthorized,
                        handler: RedirectToUnauthorizedHandler,
                    },
                ],
        }),
        NDDToastrModule,

        AppRoutingModule,
    ],
    providers: [
        QuotaParameterService,
        TermsAndPolicyGuardService,
        TermsAndPolicyService,
        {
            provide: NDDTranslationService,
            useClass: CustomTranslationService,
        },
    ],
    declarations: [
        AppComponent,
        PageNotFoundComponent,
        PageInternalServerErrorComponent,
        PageForbiddenComponent,
        PageUnauthorizedComponent,
    ],
    bootstrap: [AppComponent],
})
export class AppModule { }
